/**
 * AudioOutputNode - routes audio to the speaker output
 *
 * Endpoints:
 * - audioIn (input): audio node to route to destination
 * - level (input): volume level 0-1 (default 1.0)
 * - pan (input): stereo pan -1 (left) to +1 (right) (default 0)
 * - spatialFromSource (input): when true, use spatial metadata from sources for 3D panning
 *
 * Connect synth.audioOut -> output.audioIn to hear sound.
 */

import { AudioBaseNode, type AudioDependencies } from '../AudioBaseNode';
import { Domain } from '@nodes/Endpoint';
import type { NodeDef } from '@runtime/IRuntime';
import { audio, params } from '@nodes/TypedEndpoints';
import { AudioEndpoint } from '../../AudioEndpoint';
import { getAudioEndpoint } from '../../audioRegistry';
import type { IEndpoint } from '@/runtime/IEndpoint';
import type { Payload } from '@/graph/Payload';



const endpoints = {
  ...audio({
    audioIn: { isInput: true }
  }),
  ...params({
    level: { min: 0, max: 1, step: 0.01, value: 1 },
    pan: { min: -1, max: 1, step: 0.01, value: 0 },
    spatialFromSource: {
      label: 'Spatial',
      info: 'Use pan metadata from source nodes for panning',
      dataType: 'boolean',
      value: true
    },
    headphones: {
      label: 'Headphones',
      info: 'Use HRTF 3D spatial audio (headphones only). When off, uses stereo panning (works on all outputs).',
      dataType: 'boolean',
      value: false
    },
  })
};


export class AudioOutputNode extends AudioBaseNode<typeof endpoints> {
  private outputGain: GainNode | null = null;
  private stereoPanner: StereoPannerNode | null = null;

  // Per-source panning chains
  private directSources: Set<AudioEndpoint> = new Set();
  private spatialChains: Map<AudioEndpoint, { gain: GainNode; panner: StereoPannerNode | PannerNode; isHRTF: boolean }> = new Map();

  constructor(id: string, deps: AudioDependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();

    // Create shared chain nodes
    this.outputGain = this.audioContext.createGain();
    this.stereoPanner = this.audioContext.createStereoPanner();

    // Set initial values
    this.outputGain.gain.value = this.params.level.value ?? 1;
    this.stereoPanner.pan.value = this.params.pan.value ?? 0;

    // Shared chain: audioIn → gain → stereoPanner → destination
    this.audio.audioIn.connect(this.outputGain);
    this.outputGain.connect(this.stereoPanner);
    this.stereoPanner.connect(this.audioContext.destination);
  }

  onConnectedFrom(endpointId: string, source: IEndpoint): void {
    if (endpointId === 'audioIn' && source instanceof AudioEndpoint) {
      this.directSources.add(source);
    }
  }

  onDisconnectedFrom(endpointId: string, source: IEndpoint): void {
    if (endpointId === 'audioIn' && source instanceof AudioEndpoint) {
      this.directSources.delete(source);
      this.teardownSpatialChain(source);
    }
  }

  /**
   * Metadata arriving on audio input — handle spatial data.
   * Overrides AudioBaseNode's default forwarding (AudioOutputNode has no audio outputs).
   */
  onInternalChanged(payload: Payload, endpointId: string, source: IEndpoint): void {
    if (endpointId !== 'audioIn') return;
    if (!this.params.spatialFromSource.value) return;
    if (!(source instanceof AudioEndpoint)) return;
    if (!this.directSources.has(source)) return;

    const pan = payload?.value?.pan;
    if (pan) {
      this.setupSpatialChain(source, pan);
    } else {
      this.teardownSpatialChain(source);
    }
  }

  private setupSpatialChain(source: AudioEndpoint, pan: { x: number; y: number; z: number }): void {
    const audioInEp = getAudioEndpoint(this.deps.instancePath, this.id, 'audioIn');
    if (!audioInEp) return;

    const useHRTF = this.params.headphones.value;
    let chain = this.spatialChains.get(source);

    // Rebuild chain if headphones mode changed
    if (chain && chain.isHRTF !== useHRTF) {
      this.teardownSpatialChain(source);
      chain = undefined;
    }

    if (!chain) {
      // Mute audio on shared chain (but keep metadata propagation flowing)
      source.setAudioMuted(audioInEp, true);

      const gain = this.audioContext.createGain();
      gain.gain.value = this.params.level.value ?? 1;

      let panner: StereoPannerNode | PannerNode;
      if (useHRTF) {
        const p = this.audioContext.createPanner();
        p.panningModel = 'HRTF';
        p.distanceModel = 'inverse';
        p.refDistance = 10;
        p.rolloffFactor = 1;
        panner = p;
      } else {
        panner = this.audioContext.createStereoPanner();
      }

      // source.gainNode → gain → panner → destination
      source.gainNode.connect(gain);
      gain.connect(panner);
      panner.connect(this.audioContext.destination);

      chain = { gain, panner, isHRTF: useHRTF };
      this.spatialChains.set(source, chain);
    }

    const t = this.audioContext.currentTime;
    if (chain.isHRTF) {
      // HRTF: full 3D positioning (camera-relative coords from renderer)
      const p = chain.panner as PannerNode;
      p.positionX.setTargetAtTime(pan.x ?? 0, t, 0.02);
      p.positionY.setTargetAtTime(pan.y ?? 0, t, 0.02);
      p.positionZ.setTargetAtTime(pan.z ?? 0, t, 0.02);
    } else {
      // Stereo: camera-relative left/right pan + distance attenuation
      // stereoPan from performance view is in world units (needs /50 normalization)
      // pan.x from graph metadata is already -1..1
      const hasWorldCoords = (pan as any).stereoPan !== undefined;
      const raw = hasWorldCoords ? (pan as any).stereoPan / 50 : (pan.x ?? 0);
      const panValue = Math.max(-1, Math.min(1, raw));
      (chain.panner as StereoPannerNode).pan.setTargetAtTime(panValue, t, 0.02);

      // Distance attenuation: only when world-space distance is provided (performance view)
      if (hasWorldCoords) {
        const dist = (pan as any).dist ?? 0;
        const refDist = 10;
        const attenuation = dist > refDist ? refDist / dist : 1;
        const level = (this.params.level.value ?? 1) * attenuation;
        chain.gain.gain.setTargetAtTime(level, t, 0.02);
      }
    }
  }

  private teardownSpatialChain(source: AudioEndpoint): void {
    const chain = this.spatialChains.get(source);
    if (!chain) return;

    chain.gain.disconnect();
    chain.panner.disconnect();
    this.spatialChains.delete(source);

    // Re-enable audio on shared chain
    const audioInEp = getAudioEndpoint(this.deps.instancePath, this.id, 'audioIn');
    if (audioInEp) {
      source.setAudioMuted(audioInEp, false);
    }
  }

  onParamChanged(payload: { value: any }, endpointId: string): void {
    const value = payload.value;
    switch (endpointId) {
      case 'level':
        if (this.outputGain) this.outputGain.gain.value = value ?? 1;
        for (const chain of this.spatialChains.values()) {
          chain.gain.gain.value = value ?? 1;
        }
        break;
      case 'pan':
        if (this.stereoPanner) this.stereoPanner.pan.value = value ?? 0;
        break;
      case 'spatialFromSource':
        if (!value) {
          // Turning off: tear down all spatial chains
          for (const source of [...this.spatialChains.keys()]) {
            this.teardownSpatialChain(source);
          }
        }
        break;
      case 'headphones':
        // Rebuild all chains with new panner type on next metadata update
        // (chains auto-rebuild when isHRTF doesn't match)
        break;
    }
  }

  dispose(): void {
    // Tear down all spatial chains
    for (const source of [...this.spatialChains.keys()]) {
      this.teardownSpatialChain(source);
    }
    this.directSources.clear();

    if (this.outputGain) {
      this.audio.audioIn.disconnect(this.outputGain);
      this.outputGain.disconnect();
      this.outputGain = null;
    }
    if (this.stereoPanner) {
      this.stereoPanner.disconnect();
      this.stereoPanner = null;
    }
    super.dispose();
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const audioOutputNodeDef: NodeDef<AudioOutputNode> = {
  type: 'AudioOutput',
  shortName: 'out',
  info: 'Routes audio to the speaker output with volume, panning, and optional spatial positioning.',
  domain: Domain.Audio,
  endpoints,
  ctor: AudioOutputNode
};
